import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertCircle, Cloud, Layers, ListChecks, Mail, Radar, Receipt, AppIcon } from '../../ui/icons';
import Card from '../../ui/Card';
import Colors, { withAlpha } from '../../../constants/Colors';
import {
  opsApiHealthApi,
  OpsApiHealthFunction,
  OpsApiHealthLogRow,
} from '../../../services/api';

type HourRange = 1 | 6 | 12 | 24 | 72 | 120 | 168;

const HOUR_OPTIONS: { value: HourRange; label: string }[] = [
  { value: 1, label: '1 hour' },
  { value: 6, label: '6 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '1 day' },
  { value: 72, label: '3 days' },
  { value: 120, label: '5 days' },
  { value: 168, label: '7 days' },
];
const BAR_COUNT = 100;
const POLL_INTERVAL_MS = 60000;
const LOGS_PANEL_MAX_HEIGHT = 320;

// Sections for the monitoring list, matched on the Lambda name so dev and prod
// names (e.g. "check-game-access" / "hs-prod-check-game-access") land together.
// First match wins; anything unmatched falls into "Other APIs".
// Sections share the theme colours and are told apart by their panel, icon and title.
interface ApiGroupDef {
  title: string;
  icon: AppIcon;
  match: (name: string) => boolean;
}

const API_GROUPS: ApiGroupDef[] = [
  { title: 'Sync APIs', icon: Radar, match: (n) => n.includes('sync-detection') || n.includes('ingest-worker') },
  { title: 'Contact APIs', icon: Mail, match: (n) => n.includes('contact-and-report') },
  { title: 'List and Access APIs', icon: ListChecks, match: (n) => n.includes('scheduledgames') || n.includes('check-game-access') },
  { title: 'Purchase APIs', icon: Receipt, match: (n) => n.includes('purchase') || n.includes('events-rc') },
];
const OTHER_GROUP: Omit<ApiGroupDef, 'match'> = { title: 'Other APIs', icon: Layers };

function groupFunctions(items: OpsApiHealthFunction[]) {
  const groups = [...API_GROUPS.map(({ match, ...def }) => ({ ...def, items: [] as OpsApiHealthFunction[] })),
    { ...OTHER_GROUP, items: [] as OpsApiHealthFunction[] }];
  items.forEach((item) => {
    const name = item.function_name.toLowerCase();
    const index = API_GROUPS.findIndex((g) => g.match(name));
    groups[index === -1 ? groups.length - 1 : index].items.push(item);
  });
  return groups.filter((g) => g.items.length > 0);
}

type ApiGroup = ReturnType<typeof groupFunctions>[number];

// Split the ordered sections into two columns at the point where the row counts
// are closest, so both columns end at roughly the same height.
function splitIntoColumns(groups: ApiGroup[]): [ApiGroup[], ApiGroup[]] {
  const total = groups.reduce((sum, g) => sum + g.items.length, 0);
  let best = groups.length;
  let bestDiff = Infinity;
  let running = 0;
  for (let i = 0; i <= groups.length; i += 1) {
    const diff = Math.abs(total - 2 * running);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
    if (i < groups.length) running += groups[i].items.length;
  }
  return [groups.slice(0, best), groups.slice(best)];
}

function MetricsSummary({ item }: { item: OpsApiHealthFunction }) {
  const cells: { label: string; value: string }[] = [
    { label: 'Invocations', value: String(item.invocations ?? 0) },
    { label: 'Errors', value: String(item.errors ?? 0) },
    { label: 'Error rate', value: `${(item.error_rate ?? 0).toFixed(1)}%` },
    { label: 'Throttles', value: String(item.throttles ?? 0) },
    { label: 'Avg duration', value: `${Math.round(item.duration_avg_ms ?? 0)} ms` },
    { label: 'Max duration', value: `${Math.round(item.duration_max_ms ?? 0)} ms` },
  ];
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '18px',
        padding: '10px 4px',
        borderRadius: '8px',
        background: Colors.background,
      }}
    >
      {cells.map((cell) => (
        <div key={cell.label} style={{ minWidth: '92px' }}>
          <div style={{ fontSize: '11px', color: Colors.textLight }}>{cell.label}</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: Colors.text }}>{cell.value}</div>
        </div>
      ))}
    </div>
  );
}

const STATUS_PILL_STYLE: Record<string, { label: string; bg: string; color: string }> = {
  healthy: { label: 'Healthy', bg: 'rgba(52, 199, 89, 0.14)', color: Colors.success },
  unhealthy: { label: 'Unhealthy', bg: 'rgba(255, 59, 48, 0.14)', color: Colors.error },
  no_activity: { label: 'No activity', bg: Colors.divider, color: Colors.textLight },
  unknown: { label: 'Unknown', bg: Colors.divider, color: Colors.textLight },
};

function EnvBadge({ env }: { env?: string | null }) {
  if (!env) return null;
  const isProd = env === 'prod';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '24px',
        padding: '0 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        background: isProd ? 'rgba(255, 149, 0, 0.14)' : Colors.divider,
        color: isProd ? '#b25e00' : Colors.textLight,
      }}
    >
      {env}
    </span>
  );
}

function StatusPill({ status }: { status?: string | null }) {
  const { label, bg, color } = STATUS_PILL_STYLE[status || 'unknown'] || STATUS_PILL_STYLE.unknown;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '24px',
        padding: '0 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        background: bg,
        color,
      }}
    >
      {label}
    </span>
  );
}

function CardLogsTable({
  logs,
  loading,
}: {
  logs: OpsApiHealthLogRow[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div style={{ fontSize: '13px', color: Colors.textLight, padding: '10px 0' }}>Loading logs…</div>
    );
  }
  if (logs.length === 0) {
    return (
      <div style={{ fontSize: '13px', color: Colors.inactive, padding: '10px 0' }}>
        No log lines in this window.
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '12px',
        border: `1px solid ${Colors.divider}`,
        borderRadius: '8px',
        overflow: 'auto',
        maxHeight: LOGS_PANEL_MAX_HEIGHT,
        background: Colors.background,
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead style={{ position: 'sticky', top: 0, background: Colors.cardBackground, zIndex: 1 }}>
          <tr style={{ textAlign: 'left', color: Colors.textLight, borderBottom: `1px solid ${Colors.divider}` }}>
            <th style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>Time (UTC)</th>
            <th style={{ padding: '8px 10px' }}>Message</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((row, idx) => (
            <tr
              key={`${row.timestamp}-${idx}`}
              style={{ borderBottom: `1px solid rgba(var(--color-border-rgb), 0.08)`, verticalAlign: 'top' }}
            >
              <td
                style={{
                  padding: '8px 10px',
                  whiteSpace: 'nowrap',
                  fontFamily: "'SF Mono', monospace",
                  color: Colors.textLight,
                }}
              >
                {row.timestamp ? row.timestamp.replace('T', ' ').replace('Z', '') : '—'}
              </td>
              <td
                style={{
                  padding: '8px 10px',
                  fontFamily: "'SF Mono', monospace",
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: Colors.text,
                }}
              >
                {row.message}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FunctionRow({
  item,
  hours,
}: {
  item: OpsApiHealthFunction;
  hours: number;
}) {
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState<OpsApiHealthLogRow[]>([]);

  useEffect(() => {
    if (!logsOpen) return;
    let cancelled = false;
    (async () => {
      setLogsLoading(true);
      try {
        const response = await opsApiHealthApi.getLogs(hours, item.function_name, 200);
        if (cancelled) return;
        if (response.success && response.data?.success) {
          setLogs(response.data.logs || []);
        } else {
          setLogs([]);
        }
      } catch {
        if (!cancelled) setLogs([]);
      } finally {
        if (!cancelled) setLogsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [logsOpen, hours, item.function_name]);

  return (
    <div
      style={{
        padding: '16px 18px',
        marginBottom: '12px',
        borderRadius: '12px',
        border: `1px solid ${Colors.divider}`,
        background: Colors.cardBackground,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setLogsOpen((open) => !open)}
              title={logsOpen ? 'Hide logs' : `Show logs for ${item.function_name}`}
              style={{
                fontSize: '15px',
                fontWeight: 700,
                color: Colors.text,
                background: 'none',
                border: 0,
                padding: 0,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              {item.heading || item.function_name}
            </button>
            <StatusPill status={item.status} />
          </div>
          <div style={{ fontSize: '12px', color: Colors.textLight, marginTop: '4px' }}>
            <span style={{ fontFamily: "'SF Mono', monospace" }}>{item.function_name}</span>
            {' · '}
            {item.invocations ?? 0} invocation{(item.invocations ?? 0) === 1 ? '' : 's'}
            {(item.errors ?? 0) > 0 ? ` · ${item.errors} error${item.errors === 1 ? '' : 's'}` : ''}
            {(item.throttles ?? 0) > 0 ? ` · ${item.throttles} throttled` : ''}
            {' · '}click name to {logsOpen ? 'hide' : 'show'} logs
          </div>
        </div>
        {item.error && (
          <div style={{ fontSize: '12px', color: Colors.error, maxWidth: '42%', textAlign: 'right' }}>
            {item.error}
          </div>
        )}
      </div>
      <MetricsSummary item={item} />
      {logsOpen && <CardLogsTable logs={logs} loading={logsLoading} />}
    </div>
  );
}

export default function ApiHealthPanel() {
  const [hours, setHours] = useState<HourRange>(24);
  const [functions, setFunctions] = useState<OpsApiHealthFunction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadStatus = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    try {
      const response = await opsApiHealthApi.getStatus(hours, BAR_COUNT);
      if (response.success && response.data?.success) {
        setError(null);
        setFunctions(response.data.functions || []);
        setLastUpdated(new Date());
      } else {
        setError(response.error || response.data?.error || 'Failed to load API health');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API health');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    loadStatus(true);
  }, [loadStatus]);

  useEffect(() => {
    const timer = setInterval(() => {
      loadStatus(false);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [loadStatus]);

  return (
    <div className="section">
      <h2 className="section-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Cloud size={24} />
        API Health
        <EnvBadge env={functions[0]?.env} />
      </h2>

      <Card
        title="API Health Monitoring"
        headerAction={
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: Colors.textLight }}>
              Recent Attempts Window
              <select
                value={hours}
                onChange={(e) => setHours(Number(e.target.value) as HourRange)}
                disabled={loading}
                style={{
                  height: '30px',
                  borderRadius: '6px',
                  border: `1px solid ${Colors.divider}`,
                  padding: '0 8px',
                  background: Colors.cardBackground,
                  color: Colors.text,
                  fontSize: '12px',
                  fontWeight: 600,
                }}
              >
                {HOUR_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        }
      >
        {error && !loading && (
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <AlertCircle size={20} />
            <div>{error}</div>
          </div>
        )}

        <div style={{ fontSize: '12px', color: Colors.textLight, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {loading ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <span className="loading-spinner" aria-hidden="true" />
              Loading status…
            </span>
          ) : lastUpdated ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Activity size={14} />
              Last updated {lastUpdated.toLocaleTimeString()}
            </span>
          ) : null}
        </div>

        <div style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                minHeight: '220px',
                color: Colors.textLight,
                fontSize: '13px',
              }}
            >
              <span className="loading-spinner" aria-hidden="true" style={{ width: '28px', height: '28px' }} />
              Loading Lambda health…
            </div>
          ) : functions.length === 0 ? (
            <div style={{ fontSize: '13px', color: Colors.inactive, padding: '12px 0' }}>
              No Lambda health data loaded yet. Set API_HEALTH_LAMBDA_FUNCTIONS.
            </div>
          ) : (
            <div className="api-health-columns">
              {splitIntoColumns(groupFunctions(functions)).map((column, columnIndex) => (
                <div key={columnIndex} style={{ minWidth: 0 }}>
                  {column.map((group, index) => (
              <section
                key={group.title}
                style={{
                  marginTop: index === 0 ? 0 : '16px',
                  padding: '12px 12px 0',
                  borderRadius: '14px',
                  border: `1px solid ${withAlpha(Colors.primary, 0.22)}`,
                  borderLeft: `4px solid ${Colors.primary}`,
                  background: withAlpha(Colors.primary, 0.04),
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <span
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px',
                      borderRadius: '8px', background: withAlpha(Colors.primary, 0.12), color: Colors.primary, flex: 'none',
                    }}
                  >
                    <group.icon size={16} />
                  </span>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: Colors.primary }}>{group.title}</span>
                  <span
                    style={{
                      fontSize: '11px', fontWeight: 700, color: Colors.primary, background: withAlpha(Colors.primary, 0.12),
                      borderRadius: '999px', padding: '2px 8px',
                    }}
                  >
                    {group.items.length}
                  </span>
                </div>
                {group.items.map((item) => (
                  <FunctionRow key={item.function_name} item={item} hours={hours} />
                ))}
              </section>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
